//TODO : Refactor after ai
/**
 * Der veränderliche Datenbestand des Prototyps – wie in Fabians Prototyp
 * „Udo Jürgens“ als Store, weil es im BA-Modus keine neuen Endpunkte gibt.
 * Änderungen überleben einen Reload im selben Tab (sessionStorage); „Zurücksetzen“
 * in der Kopfzeile lädt die Beispieldaten neu. In der BA-Vorschau, wo der
 * Speicher gesperrt sein kann, fällt der Store still auf den Arbeitsspeicher zurück.
 *
 * Dazu die Rolle, aus deren Sicht man den Prototyp bedient („Ansicht als“),
 * und die letzte Rückmeldung einer Action.
 */

import { create } from "zustand";
import {
  createJSONStorage,
  persist,
  type StateStorage,
} from "zustand/middleware";
import {
  STARTBESTAND,
  rolle as rolleVon,
  type Aenderung,
  type Datenbestand,
  type Datensatz,
  type SatzAenderung,
} from "@shared/demo-data/viacid";

export interface Meldung {
  text: string;
  fehler: boolean;
  zeit: number;
}

export interface DatenState {
  bestand: Datenbestand;
  rolle: string;
  meldung: Meldung | null;
  setRolle: (kuerzel: string) => void;
  /** Wendet eine Änderung aus den Abläufen an und zeigt ihre Meldung. */
  anwenden: (aenderung: Aenderung) => void;
  aendern: (aenderung: SatzAenderung, meldung?: string) => void;
  anlegen: (slug: string, satz: Datensatz, meldung?: string) => void;
  melden: (text: string, fehler?: boolean) => void;
  meldungSchliessen: () => void;
  zuruecksetzen: () => void;
}

function wendeAn(bestand: Datenbestand, a: Aenderung): Datenbestand {
  const neu: Datenbestand = { ...bestand };
  for (const g of a.geaendert) {
    neu[g.slug] = (neu[g.slug] ?? []).map((d) =>
      d.id === g.id
        ? {
            ...d,
            status: g.status ?? d.status,
            werte: { ...d.werte, ...(g.werte ?? {}) },
            bezuege: { ...d.bezuege, ...(g.bezuege ?? {}) },
          }
        : d
    );
  }
  for (const n of a.neu) neu[n.slug] = [...(neu[n.slug] ?? []), n.satz];
  return neu;
}

/** sessionStorage, das in einer gesperrten Umgebung (Artifact-Vorschau) nicht wirft. */
const sitzung: StateStorage = {
  getItem: (k) => {
    try {
      return sessionStorage.getItem(k);
    } catch {
      return null;
    }
  },
  setItem: (k, v) => {
    try {
      sessionStorage.setItem(k, v);
    } catch {
      /* ohne Speicher bleibt der Stand nur bis zum Reload */
    }
  },
  removeItem: (k) => {
    try {
      sessionStorage.removeItem(k);
    } catch {
      /* nichts zu tun */
    }
  },
};

export const useDatenStore = create<DatenState>()(
  persist(
    (set) => ({
      bestand: STARTBESTAND,
      rolle: "VB",
      meldung: null,
      setRolle: (kuerzel) => set({ rolle: rolleVon(kuerzel) ? kuerzel : "VB" }),
      anwenden: (aenderung) =>
        set((s) => ({
          bestand: aenderung.fehler ? s.bestand : wendeAn(s.bestand, aenderung),
          meldung: {
            text: aenderung.meldung,
            fehler: Boolean(aenderung.fehler),
            zeit: Date.now(),
          },
        })),
      aendern: (aenderung, meldung) =>
        set((s) => ({
          bestand: wendeAn(s.bestand, {
            neu: [],
            geaendert: [aenderung],
            meldung: "",
          }),
          meldung: meldung
            ? { text: meldung, fehler: false, zeit: Date.now() }
            : s.meldung,
        })),
      anlegen: (slug, satz, meldung) =>
        set((s) => ({
          bestand: wendeAn(s.bestand, {
            neu: [{ slug, satz }],
            geaendert: [],
            meldung: "",
          }),
          meldung: meldung
            ? { text: meldung, fehler: false, zeit: Date.now() }
            : s.meldung,
        })),
      melden: (text, fehler = false) =>
        set({ meldung: { text, fehler, zeit: Date.now() } }),
      meldungSchliessen: () => set({ meldung: null }),
      zuruecksetzen: () =>
        set({
          bestand: STARTBESTAND,
          meldung: {
            text: "Beispieldaten zurückgesetzt.",
            fehler: false,
            zeit: Date.now(),
          },
        }),
    }),
    {
      name: "viacid-prototyp",
      version: 4,
      storage: createJSONStorage(() => sitzung),
      partialize: (s) => ({ bestand: s.bestand, rolle: s.rolle }),
    }
  )
);

/** Der Mitarbeiter, den die gewählte Rolle im Prototyp spielt. */
export const personaVon = (kuerzel: string): string | null =>
  rolleVon(kuerzel)?.persona ?? null;
